import { ApiError } from "@/lib/api";

type T = (key: string, params?: Record<string, string | number>) => string;

function waitSeconds(message: string): number | null {
    const m = /wait (\d+)s/i.exec(message);
    return m ? Number(m[1]) : null;
}

/** Non-ApiError (fetch abort, offline) -> network message. */
function isNetworkFailure(caught: unknown): boolean {
    if (caught instanceof TypeError) return true;
    const msg = caught instanceof Error ? caught.message : String(caught ?? "");
    return /fetch failed|network|load failed|failed to fetch/i.test(msg);
}

export interface LoginErrorMapped {
    text: string;
    /** 403 OTP-dulu: password benar tapi email belum verified — ke /verify-email. */
    needsVerification: boolean;
}

export function mapLoginError(caught: unknown, t: T): LoginErrorMapped {
    const fallback = { text: t("auth.signInFailed"), needsVerification: false };
    if (isNetworkFailure(caught)) return { text: t("auth.networkFailed"), needsVerification: false };
    if (caught instanceof ApiError) {
        const msg = caught.message;
        if (/invalid email\/username\/phone or password/i.test(msg))
            return { text: t("auth.invalidCredentials"), needsVerification: false };
        if (/email verification required/i.test(msg))
            return { text: t("auth.checkEmailCode", { email: "" }).trim(), needsVerification: true };
        if (/account is not active/i.test(msg))
            return { text: t("auth.accountInactive"), needsVerification: false };
        if (/invalid or expired refresh token|session expired|no session/i.test(msg))
            return { text: t("auth.sessionExpired"), needsVerification: false };
        if (/invalid google credentials/i.test(msg))
            return { text: t("auth.googleInvalid"), needsVerification: false };
        if (/different google account/i.test(msg))
            return { text: t("auth.googleDifferent"), needsVerification: false };
    }
    return fallback;
}

export function googleErrorMessage(caught: unknown, t: T): string {
    if (isNetworkFailure(caught)) return t("auth.networkFailed");
    if (caught instanceof ApiError) {
        const msg = caught.message;
        if (/account is not active/i.test(msg)) return t("auth.accountInactive");
        if (/different google account/i.test(msg)) return t("auth.googleDifferent");
        if (/invalid google credentials/i.test(msg)) return t("auth.googleInvalid");
    }
    return t("auth.googleSignInFailed");
}

export function signupErrorMessage(caught: unknown, t: T): string {
    if (isNetworkFailure(caught)) return t("auth.networkFailed");
    if (caught instanceof ApiError) {
        const msg = caught.message;
        if (/already registered$/i.test(msg) && /email.*phone|phone.*email/i.test(msg))
            return t("auth.signup.accountTaken");
        if (/email is already registered/i.test(msg)) return t("auth.signup.emailTaken");
        if (/phone number is already registered/i.test(msg)) return t("auth.signup.phoneTaken");
        if (/invalid google credentials/i.test(msg)) return t("auth.signup.googleInvalid");
        if (/google account email does not match/i.test(msg))
            return t("auth.signup.googleMismatch");
        if (/already linked to another coach/i.test(msg))
            return t("auth.signup.googleLinkedOther");
        if (/invalid request body/i.test(msg)) return t("auth.signup.failed");
    }
    return t("auth.signup.failed");
}

export function verifyErrorMessage(caught: unknown, t: T): { text: string; fatal404: boolean } {
    const fallback = { text: t("auth.verify.failed"), fatal404: false };
    if (isNetworkFailure(caught)) return { text: t("auth.networkFailed"), fatal404: false };
    if (!(caught instanceof ApiError)) return fallback;
    const msg = caught.message;
    if (caught.status === 404 || /no coach account found/i.test(msg))
        return { text: t("auth.verify.noAccount"), fatal404: true };
    if (/too many attempts/i.test(msg)) return { text: t("auth.verify.locked"), fatal404: false };
    if (/code has expired/i.test(msg)) return { text: t("auth.verify.expired"), fatal404: false };
    if (/invalid email or code/i.test(msg))
        return { text: t("auth.verify.invalidCode"), fatal404: false };
    if (/already.*verif/i.test(msg))
        return { text: t("auth.verify.alreadyVerified"), fatal404: false };
    return fallback;
}

export function resendErrorMessage(
    caught: unknown,
    t: T,
): { text: string; cooldown: number | null } {
    if (isNetworkFailure(caught)) return { text: t("auth.networkFailed"), cooldown: null };
    if (caught instanceof ApiError) {
        const msg = caught.message;
        if (caught.status === 404 || /no coach account found/i.test(msg))
            return { text: t("auth.verify.noAccount"), cooldown: null };
        if (/too many attempts/i.test(msg)) return { text: t("auth.verify.locked"), cooldown: 60 };
        if (/please wait \d+s/i.test(msg)) {
            const s = waitSeconds(msg) ?? 60;
            return { text: t("auth.verify.tooFast", { s }), cooldown: s };
        }
        if (/temporarily unavailable|failed to send|email service/i.test(msg))
            return { text: t("auth.verify.sendFailed"), cooldown: null };
    }
    return { text: t("auth.verify.failed"), cooldown: null };
}

export function extractWaitSeconds(caught: unknown): number | null {
    if (caught instanceof ApiError) return waitSeconds(caught.message);
    return null;
}

/**
 * Firebase popup error -> clear message, or null when the user just
 * closed the popup (silent, no Alert needed).
 */
export function googlePopupMessage(caught: unknown, t: T): string | null {
    const code =
        typeof caught === "object" && caught !== null
            ? String((caught as { code?: unknown }).code ?? "")
            : "";
    if (
        code === "auth/popup-closed-by-user" ||
        code === "auth/cancelled-popup-request" ||
        code === "auth/user-cancelled" ||
        /popup closed/i.test(caught instanceof Error ? caught.message : "")
    ) {
        return null;
    }
    if (code === "auth/popup-blocked") return t("auth.googleSignInFailed");
    if (code === "auth/network-request-failed") return t("auth.networkFailed");
    if (/not configured/i.test(caught instanceof Error ? caught.message : "")) {
        return t("auth.googleSignInFailed");
    }
    return googleErrorMessage(caught, t);
}
